import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiCalendar,
  FiCheck,
  FiCoffee,
  FiMessageCircle,
  FiPlayCircle,
  FiTrendingDown
} from "react-icons/fi";
import RegisterPopup from "../components/RegisterPopup";
import LoginPopup from "../components/LoginPopup";
import Dumbbell3D from "../components/Dumbbell3D";
import ThemeToggle from "../components/ThemeToggle";
import { getSession } from "../utils/session";
import {
  requestNotificationPermission,
  scheduleDailyNotifications
} from "../utils/notifications";
import "./Home.css";

const TITULAR = "Entrena con tu entrenador, no solo con una app";

const INCLUYE = [
  {
    Icono: FiActivity,
    titulo: "Rutinas hechas para ti",
    texto:
      "Tu entrenador monta el plan y lo ajusta cuando cambias de objetivo. Nada de plantillas genéricas."
  },
  {
    Icono: FiCoffee,
    titulo: "Comidas del día",
    texto:
      "Plan semanal con recetas y raciones. Apunta lo que comes desde una foto y queda registrado."
  },
  {
    Icono: FiPlayCircle,
    titulo: "Videoteca de ejercicios",
    texto:
      "Cada ejercicio con su vídeo, para que sepas cómo se hace antes de tener la barra encima."
  },
  {
    Icono: FiTrendingDown,
    titulo: "Tu peso, en una línea",
    texto:
      "Registra el peso y mira la tendencia de las últimas semanas, no el número de un mal día."
  },
  {
    Icono: FiMessageCircle,
    titulo: "Chat con tu entrenador",
    texto: "Dudas, cambios de horario o una foto de la comida. Sin salir de la app."
  },
  {
    Icono: FiCalendar,
    titulo: "Calendario de sesiones",
    texto: "Lo que toca hoy y lo que viene esta semana, con recordatorio antes de entrenar."
  }
];

const PASOS = [
  {
    titulo: "Crea tu cuenta",
    texto: "Datos básicos, peso, altura y qué quieres conseguir. Dos minutos."
  },
  {
    titulo: "Tu entrenador la aprueba",
    texto:
      "Revisa tu ficha antes de darte acceso. Por eso no entras al instante: alguien te está leyendo."
  },
  {
    titulo: "Empiezas a entrenar",
    texto: "Abres la app y ya tienes la rutina y las comidas del día esperándote."
  }
];

const MOMENTOS = [
  {
    hora: "07:30",
    etiqueta: "Desayuno",
    foto: "/img/desayuno.webp",
    titulo: "Empiezas con el desayuno puesto",
    filas: [
      { nombre: "Batido de Plátano y Avena", dato: "180 kcal", extra: "4 min" },
      { nombre: "Pan de Avena y Yogur", dato: "140 kcal", extra: "18 min" }
    ],
    pie: "38 desayunos distintos en el recetario."
  },
  {
    hora: "18:00",
    etiqueta: "Entrenamiento",
    foto: "/img/entreno.webp",
    titulo: "La sesión ya está montada",
    filas: [
      { nombre: "Press de banca", dato: "Pecho", extra: "4 × 8" },
      { nombre: "Remo con barra", dato: "Espalda", extra: "4 × 10" },
      { nombre: "Fondos en paralelas", dato: "Tríceps", extra: "3 × máx" }
    ],
    pie: "Cada ejercicio con su vídeo en la videoteca."
  },
  {
    hora: "21:00",
    etiqueta: "Cena y cierre",
    foto: "/img/cena.webp",
    titulo: "Cierras el día con dos datos",
    filas: [
      { nombre: "Wok de Pollo y Anacardos", dato: "320 kcal", extra: "15 min" },
      { nombre: "Crema de Zanahoria y Puerro", dato: "130 kcal", extra: "13 min" },
      { nombre: "Peso de hoy", dato: "81,6 kg", extra: "−0,4 kg" }
    ],
    pie: "El peso alimenta la gráfica de las últimas semanas."
  }
];

const OBJETIVOS = {
  bajar: {
    titulo: "Bajar de peso",
    cambios: [
      "Más trabajo de cardio repartido en la semana",
      "Recetas en la franja de 120 a 250 kcal",
      "Seguimiento de peso con la tendencia a la vista"
    ]
  },
  subir: {
    titulo: "Ganar masa",
    cambios: [
      "Más series y carga que sube poco a poco",
      "Recetas en la franja de 250 a 400 kcal",
      "Seguimiento de peso y de lo que levantas"
    ]
  }
};

const PREGUNTAS = [
  {
    q: "¿Por qué no entro justo después de registrarme?",
    a: "Porque tu entrenador aprueba las cuentas a mano. Revisa tu peso, tu altura y tu objetivo antes de darte acceso, y así la primera rutina que ves ya va contigo."
  },
  {
    q: "¿Necesito gimnasio?",
    a: "No para todo. El catálogo mezcla ejercicios con barra y máquina con otros de peso corporal, así que tu entrenador puede montarte la rutina con lo que tengas."
  },
  {
    q: "¿Puedo cambiar de objetivo más adelante?",
    a: "Sí. Cambias el objetivo en tu ficha y tu entrenador reajusta la rutina y el plan de comidas desde ahí."
  },
  {
    q: "¿Tengo que apuntar todo lo que como?",
    a: "Sólo si quieres. Puedes registrar una comida desde una foto y la app la reconoce, o dejarlo pasar ese día."
  },
  {
    q: "¿Funciona en el móvil?",
    a: "Sí, es lo normal. La app está pensada para el móvil y avisa antes de cada sesión."
  }
];

// Posición del cursor dentro del elemento, de -0.5 a 0.5, en --px/--py. El CSS
// deriva de ahí el foco de luz, la inclinación y el paralaje. Sin estado de
// React: se escribe en el estilo directamente para no re-renderizar. Las
// variables se heredan: lo que se pone en el hero lo leen la mancuerna y las
// fichas, y la tarjeta lo sobrescribe con el suyo propio al pasar por encima.
function seguirCursor(e) {
  if (e.pointerType !== "mouse") return;
  const el = e.currentTarget;
  const caja = el.getBoundingClientRect();
  el.style.setProperty("--px", ((e.clientX - caja.left) / caja.width - 0.5).toFixed(3));
  el.style.setProperty("--py", ((e.clientY - caja.top) / caja.height - 0.5).toFixed(3));
}

function soltarCursor(e) {
  e.currentTarget.style.removeProperty("--px");
  e.currentTarget.style.removeProperty("--py");
}

function Home() {
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [momento, setMomento] = useState(1);
  const [objetivo, setObjetivo] = useState("bajar");
  const [abierta, setAbierta] = useState(0);
  const navigate = useNavigate();

  // Activar notificaciones al entrar
  useEffect(() => {
    async function activarNotificaciones() {
      await requestNotificationPermission();
      await scheduleDailyNotifications();
    }
    activarNotificaciones();
  }, []);

  // Redirección automática si ya hay sesión
  useEffect(() => {
    getSession().then(({ token, user }) => {
      if (token && user) {
        navigate("/dashboard");
      }
    });
  }, [navigate]);

  const dia = MOMENTOS[momento];
  const plan = OBJETIVOS[objetivo];

  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="scroll-progress" aria-hidden="true" />
        <div className="landing-nav-inner">
          <a className="brand" href="#inicio">
            <span className="brand-mark">
              <img src="/titan-mark.png" alt="" />
            </span>
            <span className="brand-name">Titan Training</span>
          </a>

          <nav className="landing-links">
            <a href="#incluye">Qué incluye</a>
            <a href="#dia">Un día</a>
            <a href="#empiezas">Cómo empiezas</a>
            <a href="#preguntas">Preguntas</a>
          </nav>

          <div className="landing-nav-actions">
            <ThemeToggle />
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setShowLogin(true)}
            >
              Iniciar sesión
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowRegister(true)}
            >
              Crear cuenta
            </button>
          </div>
        </div>
      </header>

      <main id="inicio">
        <section className="hero" onPointerMove={seguirCursor} onPointerLeave={soltarCursor}>
          <div className="wrap hero-inner">
            <h1 className="hero-title">
              {TITULAR.split(" ").map((palabra, i) => (
                <span key={i}>
                  <span className="word" style={{ "--d": `${i * 55}ms` }}>
                    {palabra}
                  </span>{" "}
                </span>
              ))}
            </h1>
            <p className="hero-lead enter" style={{ "--d": "420ms" }}>
              Titan Training es donde tu entrenador te deja la rutina, las comidas y
              el seguimiento. Tú abres la app y sabes exactamente qué toca hoy.
            </p>
            <div className="hero-actions enter" style={{ "--d": "520ms" }}>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => setShowRegister(true)}
              >
                Crear cuenta
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-lg"
                onClick={() => setShowLogin(true)}
              >
                Ya tengo cuenta
              </button>
            </div>

            <div className="hero-stage">
              <Dumbbell3D className="hero-dumbbell" />

              <div className="hero-chip hero-chip-a">
                <span className="hero-chip-icono">
                  <FiCheck />
                </span>
                <span>
                  <strong>Cuenta aprobada</strong>
                  <span className="hero-chip-sub">por tu entrenador</span>
                </span>
              </div>

              <div className="hero-chip hero-chip-b">
                <svg className="hero-chip-linea" viewBox="0 0 64 24" aria-hidden="true">
                  <polyline points="2,5 14,8 24,7 34,13 44,12 54,18 62,19" />
                </svg>
                <span>
                  <strong>−0,4 kg</strong>
                  <span className="hero-chip-sub">esta semana</span>
                </span>
              </div>

              <div
                className="hero-panel"
                onPointerMove={seguirCursor}
                onPointerLeave={soltarCursor}
              >
                <div className="hero-panel-head">
                  <span className="hero-panel-day">Entrenamiento de hoy</span>
                  <span className="hero-panel-tag">Empuje</span>
                </div>
                <ul className="hero-panel-list">
                  {[
                    ["Press banca", "4 × 8 · 60 kg"],
                    ["Press militar", "3 × 10 · 30 kg"],
                    ["Fondos en paralelas", "3 × máximas"],
                    ["Extensión de tríceps", "3 × 12 · 20 kg"]
                  ].map(([nombre, detalle], i) => (
                    <li key={nombre} className="enter" style={{ "--d": `${620 + i * 70}ms` }}>
                      <span>{nombre}</span>
                      <span className="hero-panel-detail">{detalle}</span>
                    </li>
                  ))}
                </ul>
                <div className="hero-panel-foot enter" style={{ "--d": "920ms" }}>
                  <span className="hero-panel-kcal">2.140 kcal</span>
                  <span className="hero-panel-detail">objetivo de hoy</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="band" id="incluye">
          <div className="wrap">
            <h2 className="section-title reveal">Todo lo que traes encima, en un sitio</h2>
            <div className="feature-grid">
              {INCLUYE.map((item) => (
                <article
                  className="feature reveal"
                  key={item.titulo}
                  onPointerMove={seguirCursor}
                >
                  <span className="feature-icono" aria-hidden="true">
                    <item.Icono />
                  </span>
                  <h3>{item.titulo}</h3>
                  <p>{item.texto}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="dia" id="dia">
          <div className="wrap">
            <h2 className="section-title reveal">Un día cualquiera</h2>

            <div className="dia-layout reveal">
              <div className="dia-tabs" role="tablist" aria-label="Momentos del día">
                {MOMENTOS.map((m, i) => (
                  <button
                    key={m.etiqueta}
                    type="button"
                    role="tab"
                    id={`tab-${i}`}
                    aria-selected={momento === i}
                    aria-controls={`panel-${i}`}
                    className={"dia-tab" + (momento === i ? " is-active" : "")}
                    onClick={() => setMomento(i)}
                  >
                    <span className="dia-tab-hora">{m.hora}</span>
                    <span className="dia-tab-nombre">{m.etiqueta}</span>
                  </button>
                ))}
              </div>

              <div
                className="dia-panel"
                role="tabpanel"
                id={`panel-${momento}`}
                aria-labelledby={`tab-${momento}`}
              >
                <div className="dia-fotos">
                  {MOMENTOS.map((m, i) => (
                    <img
                      key={m.foto}
                      src={m.foto}
                      alt=""
                      width="1400"
                      height="933"
                      loading="lazy"
                      decoding="async"
                      className={momento === i ? "is-active" : ""}
                    />
                  ))}
                  <span className="dia-hora">{dia.hora}</span>
                </div>
                <div className="dia-contenido" key={momento}>
                  <h3>{dia.titulo}</h3>
                  <ul className="dia-filas">
                    {dia.filas.map((fila, i) => (
                      <li key={fila.nombre} style={{ "--d": `${i * 65}ms` }}>
                        <span className="dia-fila-nombre">{fila.nombre}</span>
                        <span className="dia-fila-dato">{fila.dato}</span>
                        <span className="dia-fila-extra">{fila.extra}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="dia-pie">{dia.pie}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="objetivo">
          <div className="wrap objetivo-inner">
            <div className="objetivo-texto">
              <h2 className="section-title reveal">Tu objetivo cambia el plan</h2>
              <p>
                Lo eliges al crear la cuenta y lo puedes cambiar cuando quieras. Esto es
                lo que se mueve por dentro.
              </p>
              <div className="objetivo-switch" role="group" aria-label="Objetivo">
                {Object.keys(OBJETIVOS).map((clave) => (
                  <button
                    key={clave}
                    type="button"
                    className={"objetivo-btn" + (objetivo === clave ? " is-active" : "")}
                    aria-pressed={objetivo === clave}
                    onClick={() => setObjetivo(clave)}
                  >
                    {OBJETIVOS[clave].titulo}
                  </button>
                ))}
              </div>
            </div>

            <ul className="objetivo-lista" key={objetivo}>
              {plan.cambios.map((cambio, i) => (
                <li key={cambio} style={{ "--d": `${i * 70}ms` }}>
                  {cambio}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="steps" id="empiezas">
          <div className="wrap">
            <h2 className="section-title reveal">Cómo empiezas</h2>
            <div className="steps-layout">
              <ol className="step-list">
                {PASOS.map((paso, i) => (
                  <li className="step reveal" key={paso.titulo}>
                    <span className="step-num">{i + 1}</span>
                    <div>
                      <h3>{paso.titulo}</h3>
                      <p>{paso.texto}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <figure className="steps-foto">
                <img
                  src="/img/entrenador.webp"
                  alt="Un entrenador corrige a una clienta durante un press con mancuernas"
                  width="1400"
                  height="933"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption className="steps-foto-ficha">
                  <FiCheck aria-hidden="true" />
                  Tu entrenador revisa cada ficha
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="faq" id="preguntas">
          <div className="wrap">
            <h2 className="section-title reveal">Preguntas que te vas a hacer</h2>
            <div className="faq-lista">
              {PREGUNTAS.map((item, i) => (
                <div className={"faq-item reveal" + (abierta === i ? " is-open" : "")} key={item.q}>
                  <button
                    type="button"
                    className="faq-boton"
                    aria-expanded={abierta === i}
                    onClick={() => setAbierta(abierta === i ? -1 : i)}
                  >
                    <span>{item.q}</span>
                    <span className="faq-signo" aria-hidden="true" />
                  </button>
                  <div className="faq-respuesta">
                    <p>{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="closer">
          <div className="wrap closer-inner reveal-grow">
            <h2>Tu entrenador ya está al otro lado</h2>
            <p>Crea la cuenta y te avisamos en cuanto te dé acceso.</p>
            <button
              type="button"
              className="btn btn-primary btn-lg"
              onClick={() => setShowRegister(true)}
            >
              Crear cuenta
            </button>
          </div>
        </section>
      </main>

      <footer className="landing-foot">
        <div className="wrap landing-foot-inner">
          <span>Titan Training</span>
          <span className="landing-foot-note">Entrenamiento personal</span>
        </div>
        <p className="wrap landing-creditos">
          Fotos de referencia (CC BY 2.0):{" "}
          <a href="https://commons.wikimedia.org/wiki/File:Personal_training_dumbbell_chest_press.jpg">PTPioneer</a>,{" "}
          <a href="https://commons.wikimedia.org/wiki/File:Woman_in_a_gym_sitting_on_the_floor_and_doing_dumbbell_curls.jpg">Shixart1985</a>,{" "}
          <a href="https://commons.wikimedia.org/wiki/File:Banana_Oatmeal_Breakfast_Bowl_(44302857265).jpg">Ella Olsson</a>{" "}
          y <a href="https://commons.wikimedia.org/wiki/File:Wok_Fajitas_(5921289167).jpg">Luca Nebuloni</a>,
          vía Wikimedia Commons.
        </p>
      </footer>

      {showRegister && (
        <RegisterPopup
          onClose={() => setShowRegister(false)}
          onGoToLogin={() => {
            setShowRegister(false);
            setShowLogin(true);
          }}
        />
      )}
      {showLogin && (
        <LoginPopup
          onClose={() => setShowLogin(false)}
          onGoToRegister={() => {
            setShowLogin(false);
            setShowRegister(true);
          }}
        />
      )}
    </div>
  );
}

export default Home;
